/**
 * ANALYTICS (PostHog) — camada única de saída de dados.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * POR QUE ESTE ARQUIVO EXISTE, E POR QUE NINGUÉM DEVE CHAMAR O posthog DIRETO
 * ─────────────────────────────────────────────────────────────────────────────
 * A Reinoplay é uma plataforma PARA CRIANÇAS. Sob a LGPD (art. 14), dado de criança
 * exige consentimento parental ESPECÍFICO E DESTACADO, e não pode ser compartilhado
 * com terceiro sem ele. Pior: o quiz de entrada coleta a RELIGIÃO da família — que é
 * DADO SENSÍVEL (art. 5º, II).
 *
 * Mandar isso para uma plataforma de analytics de terceiros seria um problema jurídico
 * sério — e é o tipo de coisa que entra no código sem ninguém perceber, seis meses
 * depois, quando alguém "só quis medir uma coisinha".
 *
 * Então a regra não é uma convenção — é uma TRAVA:
 *
 *   1. Toda propriedade passa por um filtro. Chave proibida (nome/idade/religião do
 *      filho, CPF, telefone, email) é DESCARTADA, com aviso no console em dev.
 *   2. Nunca identificamos ninguém. Os eventos são ANÔNIMOS ($process_person_profile:
 *      false). O único id é um UUID aleatório por navegador — não deriva de nada pessoal,
 *      só agrupa a sessão num funil.
 *      → Ainda dá FUNIL, tendências e SQL. É tudo que a gente precisa.
 *   3. Session replay nunca é ligado: falamos só com o endpoint de captura de eventos.
 *      Gravar a tela de uma criança é o pior cenário possível — jurídico e ético.
 *   4. Nada de autocapture: só eventos que a gente instrumenta de propósito, com nome
 *      limpo. Captura automática varreria textos da tela — inauditável numa plataforma
 *      infantil.
 *   5. Sem geolocalização por IP ($geoip_disable).
 *
 * O QUE MEDIMOS: a jornada do PAI (quem paga) — entrada, paywall, checkout, login.
 * O QUE NÃO MEDIMOS: quem é a criança.
 *
 * NOTA DE IMPLEMENTAÇÃO: falamos direto com o endpoint HTTP do PostHog (fetch), em vez de
 * usar a lib posthog-js — a v1.402 não enviava os eventos neste ambiente. O endpoint HTTP
 * funciona (testado). Ver iniciarAnalytics().
 */

let ligado = false;

/** Chaves que NUNCA podem sair daqui. Se aparecerem numa propriedade, são descartadas. */
const PROIBIDAS = [
  // criança
  "nome_filho", "nome_crianca", "child_name", "childName",
  "idade_filho", "child_age", "childAge", "birth_date", "birthDate",
  "religiao", "religion",
  "preferencias", "preferences",
  // pessoa
  "email", "cpf", "telefone", "phone", "name", "nome",
  "password", "token", "senha",
];

const ehProibida = (chave) => {
  const k = String(chave).toLowerCase();
  return PROIBIDAS.some((p) => k === p.toLowerCase() || k.includes(p.toLowerCase()));
};

/**
 * Filtra as propriedades. É a última linha de defesa: mesmo que alguém, daqui a um ano,
 * escreva `analytics.track('x', { nome_filho: 'Ana' })`, o dado NÃO sai daqui.
 */
function limpar(props = {}) {
  const saida = {};
  const bloqueadas = [];

  for (const [k, v] of Object.entries(props)) {
    if (ehProibida(k)) {
      bloqueadas.push(k);
      continue;
    }
    // valor objeto/array pode esconder dado pessoal dentro — só deixamos passar primitivo.
    if (v !== null && typeof v === "object") {
      bloqueadas.push(`${k} (objeto)`);
      continue;
    }
    saida[k] = v;
  }

  if (bloqueadas.length && process.env.NODE_ENV !== "production") {
    // Em dev isso PRECISA gritar: é um erro de quem escreveu o evento, não um detalhe.
    console.warn(
      `[analytics] propriedades BLOQUEADAS (dado pessoal/de criança não vai para o PostHog): ${bloqueadas.join(", ")}`
    );
  }

  return saida;
}

let apiKey = null;
let apiHost = null;
let distinctId = null;

/**
 * Um id ANÔNIMO por navegador, guardado no localStorage. É um UUID aleatório — NÃO deriva
 * de nada pessoal. Serve só pra o PostHog agrupar os eventos da mesma sessão num funil.
 * Sem ele, cada evento seria uma pessoa diferente e o funil não fecharia.
 */
function idAnonimo() {
  const CHAVE = "rp_anon_id";
  try {
    let id = localStorage.getItem(CHAVE);
    if (!id) {
      id =
        (crypto.randomUUID && crypto.randomUUID()) ||
        `anon-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(CHAVE, id);
    }
    return id;
  } catch {
    return `anon-${Date.now()}`;
  }
}

/**
 * Liga o analytics. Sem a chave no ambiente, tudo vira no-op — o app funciona igual e
 * nenhum byte sai. É assim que isto pode entrar em produção desligado.
 *
 * ⚠️ POR QUE ENVIO DIRETO POR FETCH, E NÃO PELA LIB posthog-js
 * O posthog-js@1.402 não dispara os eventos neste ambiente (inicializa, carrega o config,
 * mas o transporte interno não envia — provavelmente segurando por causa das feature
 * flags). O endpoint HTTP de captura, esse SIM funciona (testado: 200 {"status":"Ok"}).
 * Então falamos direto com ele. Menos dependência da versão da lib, e o mesmo resultado.
 */
export async function iniciarAnalytics() {
  const key = process.env.REACT_APP_POSTHOG_KEY;
  const host = process.env.REACT_APP_POSTHOG_HOST;

  if (!key || !host) return; // desligado: nada é carregado, nada é enviado

  apiKey = key;
  apiHost = host.replace(/\/$/, "");
  distinctId = idAnonimo();
  ligado = true;

  if (process.env.NODE_ENV !== "production") {
    window.__analyticsDebug = { track, tela, ligado: () => ligado, distinctId };
  }
}

/**
 * Um evento. As propriedades passam pelo filtro — sempre.
 *
 * FIRE-AND-FORGET: `keepalive` garante o envio mesmo se a pessoa fechar a aba logo depois,
 * e o erro é engolido — analytics nunca pode atrapalhar o app.
 */
export function track(evento, props = {}) {
  if (!ligado || !apiKey) return;

  const corpo = {
    api_key: apiKey,
    event: evento,
    distinct_id: distinctId,
    timestamp: new Date().toISOString(),
    properties: {
      ...limpar(props),
      // ANÔNIMO: não cria perfil de pessoa (é o equivalente HTTP do person_profiles:
      // identified_only). E sem geolocalização por IP — não vale o risco.
      $process_person_profile: false,
      $geoip_disable: true,
      $lib: "reinoplay-web",
    },
  };

  try {
    fetch(`${apiHost}/i/v0/e/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(corpo),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* analytics nunca derruba o app */
  }
}

/**
 * Uma "tela". Nome explícito, nunca a URL crua (que pode ter id de usuário/conteúdo).
 */
export function tela(nome, props = {}) {
  track("$pageview", { tela: nome, ...limpar(props) });
}

/**
 * ⚠️ NÃO EXISTE identificar(). É proposital.
 *
 * Identificar cria um perfil de pessoa no PostHog — e numa plataforma infantil isso é
 * o que a gente está tentando NÃO fazer. Se um dia for necessário (ex.: medir retenção
 * de PAGANTES ADULTOS), tem que passar por decisão consciente, com base legal e
 * consentimento — não por alguém adicionar uma linha aqui.
 */

/** Só para os testes e para o painel de debug. */
export const _estado = () => ({ ligado, chaveConfigurada: Boolean(process.env.REACT_APP_POSTHOG_KEY) });
